import React, { FC, useContext, useEffect, useState } from 'react';
import { CustomSwitch, If } from 'components';
import { EditIconBlue, OutlinedInfoIcon } from 'assets/icons';
import { ReminderConfig } from 'components/reminder-modal/types';
import { ReminderDateTypeLabel } from 'containers/settings/constants';
import { formatReminderText } from 'containers/settings/utils';
import DateUtils from 'utils/dateUtils';
import { useAppDispatch, useAppSelector } from 'hooks';
import {
  selectDueDate,
  selectInvoiceCustomer,
  selectPaymentReminders,
  selectPaymentRemindersEnabled,
  togglePaymentRemindersEnabled,
  updatePaymentReminders
} from 'containers/invoices/InvoicesSlice';
import {
  selectDueDateEditPage,
  selectInvoiceCustomerEditPage,
  selectPaymentRemindersEditPage,
  selectPaymentRemindersEnabledEditPage,
  togglePaymentRemindersEnabledEditPage,
  updatePaymentRemindersEditPage
} from 'containers/invoices/edit-invoice/invoicesEditSlice';
import EditReminderModal from 'components/reminder-modal/EditReminderModal';
import { calculateScheduledDate } from 'containers/estimates/utils';
import { validateInvoiceReminderPrerequisites } from '../util';
import { Customer } from 'containers/invoices/invoices.model';
import { InfoType, InvoiceTabEnum } from '../constants';

interface InvoiceFormRemindersSectionProps {
  isInvoiceEdit?: boolean;
  context: React.Context<any>;
}

const InvoiceFormRemindersSection: FC<InvoiceFormRemindersSectionProps> = ({ isInvoiceEdit = false, context }) => {
  const [attemptedToggle, setAttemptedToggle] = useState(false);
  const [isReminderModalOpen, setIsReminderModalOpen] = useState(false);

  const dispatch = useAppDispatch();
  const { selectedInvoiceTab, setSelectedInvoiceTab } = useContext<any>(context);

  // Select appropriate state based on create/edit mode
  const customer = useAppSelector(isInvoiceEdit ? selectInvoiceCustomerEditPage : selectInvoiceCustomer);
  const dueDate = useAppSelector(isInvoiceEdit ? selectDueDateEditPage : selectDueDate);
  const remindersEnabled = useAppSelector(
    isInvoiceEdit ? selectPaymentRemindersEnabledEditPage : selectPaymentRemindersEnabled
  );
  const reminders = useAppSelector(isInvoiceEdit ? selectPaymentRemindersEditPage : selectPaymentReminders);

  const dueDateString = dueDate || '';

  const isTabSelected = selectedInvoiceTab === InvoiceTabEnum.PAYMENT_REMINDERS;
  const { canEnable, errorMessage, msgType } = validateInvoiceReminderPrerequisites(customer as Customer);

  // Reset attemptedToggle when email becomes available or customer is removed
  useEffect(() => {
    if (canEnable) {
      setAttemptedToggle(false);
    }
  }, [canEnable]);

  const handleToggle = () => {
    if (!canEnable && !remindersEnabled) {
      // User tried to enable without email - show error
      setAttemptedToggle(true);
      return;
    }

    setSelectedInvoiceTab(InvoiceTabEnum.PAYMENT_REMINDERS);
    if (isInvoiceEdit) {
      dispatch(togglePaymentRemindersEnabledEditPage());
    } else {
      dispatch(togglePaymentRemindersEnabled());
    }
  };

  const handleOpenModal = () => {
    setSelectedInvoiceTab(InvoiceTabEnum.PAYMENT_REMINDERS);
    setIsReminderModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsReminderModalOpen(false);
  };

  const handleSaveReminders = (updatedReminders: ReminderConfig[]) => {
    if (isInvoiceEdit) {
      dispatch(updatePaymentRemindersEditPage(updatedReminders));
    } else {
      dispatch(updatePaymentReminders(updatedReminders));
    }
    setIsReminderModalOpen(false);
  };

  // Show error when: (1) customer exists and email missing OR (2) user attempted to toggle
  const shouldShowError = !canEnable || (attemptedToggle && !canEnable);

  return (
    <div className="w-full">
      <div className={`mb-2 text-xl font-bold ${isTabSelected ? 'text-primaryBtn' : 'text-heading'}`}>
        Payment reminders
      </div>
      <div
        className={`rounded-md border px-[30px] py-5 1439px:p-10 ${
          isTabSelected ? 'border-primaryBtn' : 'border-borderGray'
        }`}>
        <div className="mt-4 flex items-start gap-3">
          <CustomSwitch
            enabled={remindersEnabled}
            toggleSwitch={handleToggle}
            id={`${isInvoiceEdit ? 'edit' : 'create'}-invoice-reminders-switch`}
            className="mt-1"
          />
          <div className="flex-1">
            <div className={`text-sbase font-semibold text-primaryText ${remindersEnabled ? '' : 'opacity-70'}`}>
              Send payment reminders for this invoice
            </div>
            {/* Show error when customer exists with missing email OR user tried to toggle */}
            <If condition={shouldShowError}>
              <div
                className={`mt-2 flex w-fit items-center gap-[5px] rounded-md ${
                  msgType === InfoType.WARN ? 'border border-secondary bg-[#A4D7FA4D]' : ''
                } py-1 px-[5.5px]`}>
                <OutlinedInfoIcon
                  className={`mt-0.5 h-[15px] w-[15px] shrink-0 ${
                    msgType === InfoType.ERROR ? 'path path-fill-current text-error' : ''
                  }`}
                />
                <span className={`text-sbase ${msgType === InfoType.ERROR ? 'text-error' : 'text-primaryText'}`}>
                  {errorMessage}
                </span>
              </div>
            </If>
          </div>
        </div>

        <If condition={remindersEnabled}>
          <div className="mt-4 rounded-lg border border-borderGray bg-white px-4 pb-5 pt-2">
            <>
              {reminders.length > 0 ? (
                reminders.map((reminder, index) => {
                  // Use reminderDate from response if available, otherwise calculate it
                  const scheduledDate =
                    reminder.reminderDate ||
                    (dueDateString
                      ? calculateScheduledDate(dueDateString, reminder.scheduleRelation, reminder.days)
                      : null);
                  const isNotLast = index < reminders.length - 1;
                  return (
                    <div
                      key={reminder.id}
                      className={`grid grid-cols-[auto_1fr_auto] gap-4 py-3 ${
                        isNotLast ? 'border-b border-borderGray' : ''
                      }`}>
                      <span className="text-sbase font-medium text-primaryText">
                        Reminder {reminder.reminderNumber}
                      </span>
                      <span className="text-center text-sbase text-accentText">
                        {formatReminderText(reminder, ReminderDateTypeLabel.DUE_DATE)}
                      </span>
                      <span className="text-sbase text-primaryText">
                        {scheduledDate
                          ? DateUtils.getDateInFormat({
                              date: DateUtils.parseISODateString(scheduledDate),
                              dateFormat: 'MMMM dd, yyyy'
                            })
                          : '-'}
                      </span>
                    </div>
                  );
                })
              ) : (
                <div className="py-4 text-center">
                  <span className="text-sbase text-accentText">No reminders configured</span>
                </div>
              )}
              <div className="flex justify-end">
                <button
                  onClick={handleOpenModal}
                  className="mt-2 flex items-center gap-1 text-sbase font-semibold
                   text-secondary hover:text-secondary/80">
                  <EditIconBlue className="h-4 w-4" />
                  {reminders?.length > 0 ? 'Edit reminders' : 'Add reminders'}
                </button>
              </div>
            </>
          </div>
        </If>
      </div>

      <EditReminderModal
        isOpen={isReminderModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveReminders}
        initialReminders={reminders}
        referenceDate={dueDateString}
        title="Payment reminders"
        subtitle="Reminders will be sent to customer's email"
        dateType={ReminderDateTypeLabel.DUE_DATE}
      />
    </div>
  );
};

export default InvoiceFormRemindersSection;
